import * as React from 'react';
import * as _ from 'lodash';

import { MuiThemeProvider, createMuiTheme, makeStyles } from '@material-ui/core/styles';

import {
    Grid,
    Paper, Card, Box,
    Typography,
    AppBar, Tabs, Tab, TabProps,
    FormControl, InputLabel, Select, MenuItem, TextField, FormHelperText,
    InputAdornment, IconButton, IconButtonProps,
    Button,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
    Collapse,
    Chip
} from '@material-ui/core';
import {
    Alert, AlertTitle
} from '@material-ui/lab';
import { Info as InfoIcon } from '@material-ui/icons';

import { useSnackbar } from 'notistack';

import * as Wellspring from '@greenheck/wellspring';

import { useDebounce } from '../lib/debounce';

import * as ConfigurationCore from '../lib/services/configuration.core';
import * as ConfigurationLayout from '../lib/services/configuration.projectionLayout';

const styles = makeStyles((theme) => ({
    groupHeader: {
        paddingBottom: theme.spacing(1)
    },
    paperContainer: {
        padding: theme.spacing(1)
    },
    textarea: {
        resize: "vertical"
    },

    validChip: {
        backgroundColor: theme.palette.success.dark
    },
    invalidChip: {
        backgroundColor: theme.palette.error.dark
    },
    incompleteChip: {
        backgroundColor: theme.palette.warning.dark
    }
}));

function parseSeverity(state: ConfigurationLayout.ValidationMessageStates) {
    switch (state) {
        case ConfigurationLayout.ValidationMessageStates.SoftInvalid:
            return 'warning';
        case ConfigurationLayout.ValidationMessageStates.HardInvalid:
            return 'error';
        default:
            return 'info'
    }
}

export interface ValidationMessageAlertProps {
    validationMessage: ConfigurationLayout.ValidationMessage;
}
export function ValidationMessageAlert({ validationMessage }: ValidationMessageAlertProps) {
    const [ show, setShow ] = React.useState<boolean>();
    React.useEffect(() => {
        setShow(true);
        if (validationMessage.transient) {
            setTimeout(() => {
                setShow(false);
            }, 5000);
        }
    }, [ validationMessage ]);

    return (
        <React.Fragment>
            <Collapse in={show}>
                <Alert severity={parseSeverity(validationMessage.type)}>{validationMessage.message}</Alert>
            </Collapse>
        </React.Fragment>
    );
}

interface TabPanelProps {
    value?: string;
    index: string;
}
function TabPanel({ value, index, children }: React.PropsWithChildren<TabPanelProps>) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            <Box p={1}>
                {children}
            </Box>
        </div>
    );
}

export interface InfoClickEventArgs {
    question: ConfigurationLayout.AQuestion;
}

export interface LayoutEntityProps {
    entity: ConfigurationLayout.Entity;
    coreEntity?: ConfigurationCore.Entity;
    debugging?: boolean;

    state?: Record<string, string>;
    onChange?: (e: { state: Record<string, string> }) => void;
    onInfoClick?: (e: InfoClickEventArgs) => void;
}
export function LayoutEntity({ entity, state, debugging, onChange, onInfoClick }: LayoutEntityProps) {
    const classes = styles();

    const { enqueueSnackbar } = useSnackbar();

    const [ entityState, setEntityState ] = React.useState<Record<string, string>>({});
    React.useEffect(() => {
        setEntityState(state ?? {});
    }, [ entity, state ]);
    const onQuestionChange: QuestionProps['onChange'] = (e) => {
        setEntityState(prev => ({
            ...prev,
            [e.question.source]: e.value
        }));

        onChange?.({
            // entity,
            state: {
                ...entityState,
                [e.question.source]: e.value
            }
        });
    }

    React.useEffect(() => {
        const validationMessages = entity.validationMessages.filter(v => v.transient);
        for (const validationMessage of validationMessages) {
            enqueueSnackbar(validationMessage.message, { variant: parseSeverity(validationMessage.type) });
        }
    }, [ entity ]);

    const onGroupInfoClick: (group: ConfigurationLayout.Group) => IconButtonProps['onClick'] = (group) => () => {
        // onInfoClick?.({ group });
        setInspectedQuestion(group);
    }

    const [ inspectedQuestion, setInspectedQuestion ] = React.useState<Wellspring.IModel | null>(null);
    const onQuestionInfoClick: (question: ConfigurationLayout.AQuestion) => QuestionProps['onInfoClick'] = (question: ConfigurationLayout.AQuestion) => () => {
        onInfoClick?.({ question });
        setInspectedQuestion(question);
    }

    const StateChip = () => {
        switch (entity.state) {
            case ConfigurationCore.EntityStates.Valid:
                return <Chip label="Valid" color="primary" className={classes.validChip} />;
            case ConfigurationCore.EntityStates.Invalid:
                return <Chip label="Invalid" color="primary" className={classes.invalidChip} />;
            case ConfigurationCore.EntityStates.Incomplete:
                return <Chip label="Incomplete" color="primary" className={classes.incompleteChip} />;
            default:
                return <Chip label="Unknown" />;
        }
    };

    const flattenedGroups: ConfigurationLayout.Group[] = [];
    (function flattenGroups(groups) {
        for (const group of groups) {
            flattenedGroups.push(group);

            flattenGroups(group.groups);
        }
    })(entity.groups);

    const [ currentTab, setCurrentTab ] = React.useState<string>();
    React.useEffect(() => {
        if (!currentTab && entity.layouts.length)
            setCurrentTab(entity.layouts[0].name);
    }, [ entity ]);

    return (
        <React.Fragment>
            <Grid container spacing={1} direction="column">
                <Grid item spacing={1}>
                    <StateChip />
                </Grid>
                <Grid item spacing={1}>
                    {entity.layouts.length ? (
                        <React.Fragment>
                            <AppBar position="static">
                                <Tabs value={currentTab} onChange={(e, value) => { setCurrentTab(value) }}>
                                    {entity.layouts.map(layout => <Tab label={layout.displayName} value={layout.name} />)}
                                </Tabs>
                            </AppBar>

                            {entity.layouts.map(layout => (
                                <TabPanel index={layout.name} value={currentTab}>
                                    <LayoutEntity entity={layout.layout} onChange={onChange} debugging={debugging} />
                                </TabPanel>
                            ))}
                        </React.Fragment>
                    ) : null}
                </Grid>
                <Grid item container spacing={1} direction="column">
                    {entity.validationMessages.filter(m => !m.transient).map(validationMessage => (
                        <Grid item>
                            <ValidationMessageAlert validationMessage={validationMessage} />
                        </Grid>
                    ))}
                </Grid>
                <Grid item container spacing={1} direction="column">
                    {flattenedGroups.map(group => (group.visible || debugging) && (
                        <Grid item>
                            <Paper className={classes.paperContainer}>
                                <Typography variant="h5" className={classes.groupHeader}>{group.displayName}<IconButton onClick={onGroupInfoClick(group)}><InfoIcon /></IconButton></Typography>

                                <Grid container spacing={1}>
                                    {group.questions.map(question => (question.visible || debugging) && (
                                        <Grid item xs={4}>
                                            <Question group={group} question={question} value={entityState[question.source]} onChange={onQuestionChange} onInfoClick={onQuestionInfoClick(question)} debugging={debugging} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Grid>

            <Dialog open={!!inspectedQuestion} onClose={() => setInspectedQuestion(null)} fullWidth maxWidth="md">
                <DialogTitle>
                    {inspectedQuestion?.getModelName()} Information
                </DialogTitle>
                <DialogContent>
                    {inspectedQuestion && (
                        <TextField
                            inputProps={{ className: classes.textarea }}
                            disabled
                            fullWidth
                            variant="outlined"
                            label="JSON Information"
                            InputLabelProps={{ shrink: true }}
                            margin="dense"
                            multiline
                            rows={20}
                            value={JSON.stringify(inspectedQuestion, null, 2)}
                        />
                    )}
                    {/* {inspectedQuestion && <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Property</TableCell>
                                    <TableCell>Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {inspectedQuestion.getDefinedPropertyNames().map(propertyName => (
                                    <TableRow>
                                        <TableCell>{propertyName}</TableCell>
                                        <TableCell>{inspectedQuestion.getPropertyValue(propertyName)?.toString()}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>} */}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInspectedQuestion(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    )
}

interface QuestionProps<T extends ConfigurationLayout.AQuestion = ConfigurationLayout.AQuestion> {
    group: ConfigurationLayout.Group;
    question: T;
    debugging?: boolean;

    value?: string;
    onChange?: (e: { group: ConfigurationLayout.Group, question: ConfigurationLayout.AQuestion, value: string}) => void;
    onInfoClick?: () => void;
}
function Question({ question, ...props }: QuestionProps) {
    if (question instanceof ConfigurationLayout.StringQuestion) {
        return <StringQuestion question={question} {...props} />
    }
    else if (question instanceof ConfigurationLayout.NumericQuestion) {
        return <NumericQuestion question={question} {...props} />
    }
    else if (question instanceof ConfigurationLayout.BooleanQuestion) {
        return <BooleanQuestion question={question} {...props} />
    }
    else if (question instanceof ConfigurationLayout.StringEnumerableQuestion) {
        return <StringEnumerableQuestion question={question} {...props} />
    }
    else if (question instanceof ConfigurationLayout.NumericEnumerableQuestion) {
        return <NumericEnumerableQuestion question={question} {...props} />
    }
    else {
        return <TextField label={question.name} disabled value=" " fullWidth />;
    }
}

function StringQuestion({ group, question, value, debugging, onChange, onInfoClick }: QuestionProps<ConfigurationLayout.StringQuestion>) {
    const [ currentValue, setCurrentValue ] = React.useState<string>();
    React.useEffect(() => {
        setCurrentValue(value);
    }, [ value ]);

    const debouncedCurrentValue = useDebounce(currentValue, 500);
    React.useEffect(() => {
        if (debouncedCurrentValue === undefined)
            return;

        onChange?.({
            group,
            question,
            value: debouncedCurrentValue || '<RESET>'
        });
    }, [ debouncedCurrentValue ]);

    const onTextChange = (e: React.ChangeEvent<{ value: string }>) => {
        setCurrentValue(e.target.value);
    }

    const endAdornment = (
        <InputAdornment position="start">
            <IconButton edge="start" onClick={onInfoClick}>
                <InfoIcon />
            </IconButton>
        </InputAdornment>
    );

    return <TextField disabled={debugging ? false : !question.enabled} InputLabelProps={{ shrink: true }} label={`${question.displayName}${question.assignmentState !== ConfigurationCore.AssignmentStates.Unassigned ? ' [' + ConfigurationCore.AssignmentStates[question.assignmentState] + ']' : ''}`} value={currentValue ?? value ?? (question.isValueDefined ? question.value : undefined) ?? ''} onChange={onTextChange} fullWidth error={question.validationMessages.length > 0} helperText={question.validationMessages.map(v => v.message).join('\n')} InputProps={{ endAdornment }} />;
}

function NumericQuestion({ group, question, value, debugging, onChange, onInfoClick }: QuestionProps<ConfigurationLayout.NumericQuestion>) {
    const [ currentValue, setCurrentValue ] = React.useState<string>();
    React.useEffect(() => {
        setCurrentValue(value);
    }, [ value ]);

    const debouncedCurrentValue = useDebounce(currentValue, 500);
    React.useEffect(() => {
        if (debouncedCurrentValue === undefined)
            return;

        onChange?.({
            group,
            question,
            value: debouncedCurrentValue || '<RESET>'
        });
    }, [ debouncedCurrentValue ]);

    const onTextChange = (e: React.ChangeEvent<{ value: string }>) => {
        setCurrentValue(e.target.value);
    }

    const endAdornment = (
        <InputAdornment position="start">
            <IconButton edge="start" onClick={onInfoClick}>
                <InfoIcon />
            </IconButton>
        </InputAdornment>
    );

    return <TextField disabled={debugging ? false : !question.enabled} InputLabelProps={{ shrink: true }} placeholder="Value" label={`${question.displayName}${question.assignmentState !== ConfigurationCore.AssignmentStates.Unassigned ? ' [' + ConfigurationCore.AssignmentStates[question.assignmentState] + ']' : ''}`} value={currentValue ?? value ?? (question.isValueDefined ? question.value?.toString() : undefined) ?? ''} onChange={onTextChange} fullWidth error={question.validationMessages.length > 0} helperText={question.validationMessages.map(v => v.message).join('\n')} InputProps={{ endAdornment }} />;
}

function BooleanQuestion({ group, question, value, debugging, onChange, onInfoClick }: QuestionProps<ConfigurationLayout.BooleanQuestion>) {
    const onSelectChange = (e: React.ChangeEvent<{ value: string }>) => {
        onChange?.({
            group,
            question,
            value: e.target.value
        });
    }

    const endAdornment = (
        <InputAdornment position="start">
            <IconButton edge="start" onClick={onInfoClick}>
                <InfoIcon />
            </IconButton>
        </InputAdornment>
    );

    return (
        <FormControl fullWidth error={question.validationMessages.length > 0} disabled={debugging ? false : !question.enabled}>
            <InputLabel shrink id={`layout-${group.name}-${question.name}-label`}>{question.displayName}{question.assignmentState !== ConfigurationCore.AssignmentStates.Unassigned ? ' [' + ConfigurationCore.AssignmentStates[question.assignmentState] + ']' : ''}</InputLabel>
            <Select placeholder="Value" labelId={`layout-${group.name}-${question.name}-label`} id={`layout-${group.name}-${question.name}-select`} value={value ?? (question.isValueDefined ? question.value : undefined) ?? ''} onChange={onSelectChange} endAdornment={endAdornment}>
                <MenuItem value="<RESET>">{`<Reset>`}</MenuItem>
                <MenuItem value={"true"}>Yes</MenuItem>
                <MenuItem value={"false"}>No</MenuItem>
            </Select>
            {question.validationMessages && <FormHelperText>{question.validationMessages.map(v => v.message).join('\n')}</FormHelperText>}
        </FormControl>
    );
}

function StringEnumerableQuestion({ group, question, value, debugging, onChange, onInfoClick }: QuestionProps<ConfigurationLayout.StringEnumerableQuestion>) {
    const onSelectChange = (e: React.ChangeEvent<{ value: string }>) => {
        console.log('ONCHANGE')
        onChange?.({
            group,
            question,
            value: e.target.value
        });
    }

    const endAdornment = (
        <InputAdornment position="start">
            <IconButton edge="start" onClick={onInfoClick}>
                <InfoIcon />
            </IconButton>
        </InputAdornment>
    );

    const onClickTest = (e: any) => {
        console.log('ITEM CLICKED!!!', e, value ?? (question.isValueDefined ? question.value : undefined) ?? '')
        onChange?.({
            group,
            question,
            value: sanitizedValue
        });
    }

    const sanitizedValue = value ?? (question.isValueDefined ? question.value : undefined) ?? '';

    return (
        <FormControl fullWidth error={question.validationMessages.length > 0} disabled={debugging ? false : !question.enabled}>
            <InputLabel shrink id={`layout-${group.name}-${question.name}-label`}>{question.displayName}{question.assignmentState !== ConfigurationCore.AssignmentStates.Unassigned ? ' [' + ConfigurationCore.AssignmentStates[question.assignmentState] + ']' : ''}</InputLabel>
            <Select placeholder="Value" labelId={`layout-${group.name}-${question.name}-label`} id={`layout-${group.name}-${question.name}-select`} value={sanitizedValue} onChange={onSelectChange} endAdornment={endAdornment} onContextMenu={(e) => { e.preventDefault(); console.log('TEST') }}>
                <MenuItem value="<RESET>">{`<Reset>`}</MenuItem>
                {/* <MenuItem value="null">{`<Null>`}</MenuItem> */}
                {question.answers.map(answer => <MenuItem onClick={answer.value === sanitizedValue ? onClickTest : undefined} value={answer.value}>{answer.displayName} [{ConfigurationCore.OptionStates[answer.assignmentState]}]</MenuItem>)}
            </Select>
            {question.validationMessages && <FormHelperText>{question.validationMessages.map(v => v.message).join('\n')}</FormHelperText>}
        </FormControl>
    );
}

function NumericEnumerableQuestion({ group, question, value, debugging, onChange, onInfoClick }: QuestionProps<ConfigurationLayout.NumericEnumerableQuestion>) {
    const onSelectChange = (e: React.ChangeEvent<{ value: string }>) => {
        onChange?.({
            group,
            question,
            value: e.target.value
        });
    }

    const endAdornment = (
        <InputAdornment position="start">
            <IconButton edge="start" onClick={onInfoClick}>
                <InfoIcon />
            </IconButton>
        </InputAdornment>
    );

    return (
        <FormControl fullWidth error={question.validationMessages.length > 0} disabled={debugging ? false : !question.enabled}>
            <InputLabel shrink id={`layout-${group.name}-${question.name}-label`}>{question.displayName}{question.assignmentState !== ConfigurationCore.AssignmentStates.Unassigned ? ' [' + ConfigurationCore.AssignmentStates[question.assignmentState] + ']' : ''}</InputLabel>
            <Select placeholder="Value" labelId={`layout-${group.name}-${question.name}-label`} id={`layout-${group.name}-${question.name}-select`} value={value ?? (question.isValueDefined ? question.value?.toString() : undefined) ?? ''} onChange={onSelectChange} endAdornment={endAdornment}>
                <MenuItem value="<RESET>">{`<Reset>`}</MenuItem>
                {/* <MenuItem value="null">{`<Null>`}</MenuItem> */}
                {question.answers.map(answer => <MenuItem value={answer.value.toString()}>{answer.displayName} [{ConfigurationCore.OptionStates[answer.assignmentState]}]</MenuItem>)}
            </Select>
            {question.validationMessages && <FormHelperText>{question.validationMessages.map(v => v.message).join('\n')}</FormHelperText>}
        </FormControl>
    );
}